import { View } from "@westpierstudio/luma/mvc";
import { GameServices } from "game/GameServices";
import { Ease, Tween } from "@westpierstudio/luma/createjs";
import { Signal } from "type-signals";
import { Spine } from "pixi-spine";
import { Container, Sprite } from "pixi.js";

export enum PanelState {
    PASSIVE = `PASSIVE`,
    ANIMATING_IN = `ANIMATING_IN`,
    BOUNCE = `BOUNCE`,
    BOUNCE_CLOSE = `BOUNCE_CLOSE`,
    CLOSED = `CLOSED`,
    ANIMATING_OUT = `ANIMATING_OUT`
}

export enum CharacterState {
    INTRO = `INTRO`,
    WIN = `WIN`
}

class TransitionPanelSignals {
    animationInComplete = new Signal();
    animationOutComplete = new Signal();
    transitionIn = new Signal();
    transitionOut = new Signal();
    transitionInSound = new Signal();
    transitionOutSound = new Signal();
    dragonRoar = new Signal();
    pandaRoar = new Signal();
}

export class TransitionPanel extends View {
    private backgroundContainer: Container;
    private panels: {
        leftPanelTop: Sprite;
        leftPanelBottom: Sprite;
        rightPanelTop: Sprite;
        rightPanelBottom: Sprite;
    };
    private openedPos!: {
        leftX: number;
        rightX: number;
    };
    private closedPos!: {
        leftX: number;
        rightX: number;
    };
    private bouncePos!: {
        leftX: number;
        rightX: number;
    };
    private godRays!: Spine & Container;
    private dragonTransition!: Spine & Container;
    private pandaTransition!: Spine & Container;
    private state: PanelState = PanelState.PASSIVE;
    private characterState: CharacterState = CharacterState.INTRO;

    protected leftTopTween?: createjs.Tween;
    protected leftBottomTween?: createjs.Tween;
    protected rightTopTween?: createjs.Tween;
    protected rightBottomTween?: createjs.Tween;

    signals = new TransitionPanelSignals();

    constructor(protected override readonly services: GameServices) {
        super(services);

        const {factory, layout, resources} = this.services;

        this.backgroundContainer = factory.createContainer({alpha: 0}, this);

        let spec = resources.getSpec(`vsscreen`);

        this.panels = {
            rightPanelTop: factory.createSprite(spec.transitionIn.inRightTop, this.backgroundContainer),
            rightPanelBottom: factory.createSprite(spec.transitionIn.inRightBottom, this.backgroundContainer),
            leftPanelTop: factory.createSprite(spec.transitionIn.inLeftTop, this.backgroundContainer),
            leftPanelBottom: factory.createSprite(spec.transitionIn.inLeftBottom, this.backgroundContainer),
        };

        this.resize();
        layout.onResize.add(() => this.resize());
        this.setToOnScreen();

        spec = resources.getSpec(`transitionPanel`);

        this.godRays = factory.createSpine(spec.godRays, this.backgroundContainer);
        this.godRays.visible = false;

        this.dragonTransition = factory.createSpine(spec.dragonTransition, this.backgroundContainer);
        this.pandaTransition = factory.createSpine(spec.pandaTransition, this.backgroundContainer);
        this.dragonTransition.visible = false;
        this.pandaTransition.visible = false;

        this.visible = false;

        this.positionCharactersX();
    }

    override reset(): void {
        this.skip();
    }

    private resize(): void {

        const bounds = this.services.layout.getScreenBoundaryInBucketSpace();
        const {width, height, top} = bounds;
        const ratio = width / height;

        this.positionCharactersX();

        if (ratio > 1.459) {
            this.panels.leftPanelTop.width = width / 1.9;
            this.panels.leftPanelTop.scale.y = this.panels.leftPanelTop.scale.x;
            this.panels.leftPanelBottom.width = this.panels.leftPanelTop.width;
            this.panels.leftPanelBottom.scale.y = this.panels.leftPanelBottom.scale.x;

            this.panels.rightPanelTop.width = this.panels.leftPanelTop.width;
            this.panels.rightPanelTop.scale.y = -this.panels.rightPanelTop.scale.x;
            this.panels.rightPanelBottom.width = this.panels.rightPanelTop.width;
            this.panels.rightPanelBottom.scale.y = -this.panels.rightPanelBottom.scale.x;
        } else {
            this.panels.leftPanelTop.height = height / 2;
            this.panels.leftPanelTop.scale.x = this.panels.leftPanelTop.scale.y;
            this.panels.leftPanelBottom.height = this.panels.leftPanelTop.height;
            this.panels.leftPanelBottom.scale.x = this.panels.leftPanelBottom.scale.y;

            this.panels.rightPanelTop.height = this.panels.leftPanelTop.height;
            this.panels.rightPanelTop.scale.x = -this.panels.rightPanelTop.scale.y;
            this.panels.rightPanelBottom.height = this.panels.rightPanelTop.height;
            this.panels.rightPanelBottom.scale.x = -this.panels.rightPanelBottom.scale.y;
        }

        this.panels.leftPanelTop.y = top;
        this.panels.leftPanelBottom.y = top + this.panels.leftPanelTop.height;

        this.panels.rightPanelTop.y = top;
        this.panels.rightPanelBottom.y = top + this.panels.rightPanelTop.height;

        this.calculatePositions();

        if (this.leftTopTween) {
            if (this.state === PanelState.ANIMATING_IN) {
                this.animateIn(this.leftTopTween.position);
            } else if (this.state === PanelState.BOUNCE) {
                this.animateBounce(this.leftTopTween.position);
            } else if (this.state === PanelState.BOUNCE_CLOSE) {
                this.animateBounceClose(this.leftTopTween.position);
            } else if (this.state === PanelState.ANIMATING_OUT) {
                this.animateOut(this.leftTopTween.position);
            }
        } else if (this.state === PanelState.CLOSED) {
            this.setToOnScreen();
        }
    }

    skip(): void {

        Tween.removeTweens(this);
        Tween.removeTweens(this.panels.leftPanelTop);
        Tween.removeTweens(this.panels.leftPanelBottom);
        Tween.removeTweens(this.panels.rightPanelTop);
        Tween.removeTweens(this.panels.rightPanelBottom);

        this.setToOffScreen();
        this.godRays.visible = false;
        this.dragonTransition.visible = false;
        this.pandaTransition.visible = false;
        this.visible = false;
        this.state = PanelState.PASSIVE;

        this.signals.animationInComplete.dispatch();
        this.signals.transitionOut.dispatch();
        this.signals.animationOutComplete.dispatch();
    }

    private setToOnScreen(): void {
        this.panels.leftPanelTop.x = this.closedPos.leftX;
        this.panels.leftPanelBottom.x = this.closedPos.leftX;

        this.panels.rightPanelTop.x = this.closedPos.rightX;
        this.panels.rightPanelBottom.x = this.closedPos.rightX;
    }

    private setToOffScreen(): void {
        this.panels.leftPanelTop.x = this.openedPos.leftX;
        this.panels.leftPanelBottom.x = this.openedPos.leftX;

        this.panels.rightPanelTop.x = this.openedPos.rightX;
        this.panels.rightPanelBottom.x = this.openedPos.rightX;
    }

    private setToBouncePos(): void {
        this.panels.leftPanelTop.x = this.bouncePos.leftX;
        this.panels.leftPanelBottom.x = this.bouncePos.leftX;

        this.panels.rightPanelTop.x = this.bouncePos.rightX;
        this.panels.rightPanelBottom.x = this.bouncePos.rightX;
    }

    animateIn(position: number = 0) {

        const {close} = this.services.resources.getSetting(`transitionPanel`);

        this.setToOffScreen();

        this.state = PanelState.ANIMATING_IN;
        this.visible = true;

        this.leftTopTween = Tween.get(this.panels.leftPanelTop, {override: true})
        .to({x: this.closedPos.leftX}, close.close, Ease.quadInOut)
        .call(() => {
            this.leftTopTween = undefined;
            this.animateBounce();
        });
        this.leftBottomTween = Tween.get(this.panels.leftPanelBottom, {override: true})
        .to({x: this.closedPos.leftX}, close.close, Ease.quadInOut)
        .call(() => {
            this.leftBottomTween = undefined;
        });

        this.rightTopTween = Tween.get(this.panels.rightPanelTop, {override: true})
        .to({x: this.closedPos.rightX}, close.close, Ease.quadInOut)
        .call(() => {
            this.rightTopTween = undefined;
        });
        this.rightBottomTween = Tween.get(this.panels.rightPanelBottom, {override: true})
        .to({x: this.closedPos.rightX}, close.close, Ease.quadInOut)
        .call(() => {
            this.rightBottomTween = undefined;
        });

        this.leftTopTween.setPosition(position);
        this.leftBottomTween.setPosition(position);
        this.rightTopTween.setPosition(position);
        this.rightBottomTween.setPosition(position);
    }

    animateBounce(position: number = 0) {

        const {close} = this.services.resources.getSetting(`transitionPanel`);

        this.setToOnScreen();

        this.state = PanelState.BOUNCE;
        this.visible = true;

        this.leftTopTween = Tween.get(this.panels.leftPanelTop, {override: true})
        .to({x: this.bouncePos.leftX}, close.bounce, Ease.quadInOut)
        .call(() => {
            this.leftTopTween = undefined;
            this.animateBounceClose();
        });
        this.leftBottomTween = Tween.get(this.panels.leftPanelBottom, {override: true})
        .to({x: this.bouncePos.leftX}, close.bounce, Ease.quadInOut)
        .call(() => {
            this.leftBottomTween = undefined;
        });

        this.rightTopTween = Tween.get(this.panels.rightPanelTop, {override: true})
        .to({x: this.bouncePos.rightX}, close.bounce, Ease.quadInOut)
        .call(() => {
            this.rightTopTween = undefined;
        });
        this.rightBottomTween = Tween.get(this.panels.rightPanelBottom, {override: true})
        .to({x: this.bouncePos.rightX}, close.bounce, Ease.quadInOut)
        .call(() => {
            this.rightBottomTween = undefined;
        });

        this.leftTopTween.setPosition(position);
        this.leftBottomTween.setPosition(position);
        this.rightTopTween.setPosition(position);
        this.rightBottomTween.setPosition(position);
    }

    animateBounceClose(position: number = 0) {

        const {close} = this.services.resources.getSetting(`transitionPanel`);

        this.setToBouncePos();

        this.state = PanelState.BOUNCE_CLOSE;
        this.visible = true;

        this.leftTopTween = Tween.get(this.panels.leftPanelTop, {override: true})
        .to({x: this.closedPos.leftX}, close.bounceClose, Ease.quadInOut)
        .call(() => {
            this.leftTopTween = undefined;
            this.signals.animationInComplete.dispatch();
            this.animateClosed();
        });
        this.leftBottomTween = Tween.get(this.panels.leftPanelBottom, {override: true})
        .to({x: this.closedPos.leftX}, close.bounceClose, Ease.quadInOut)
        .call(() => {
            this.leftBottomTween = undefined;
        });

        this.rightTopTween = Tween.get(this.panels.rightPanelTop, {override: true})
        .to({x: this.closedPos.rightX}, close.bounceClose, Ease.quadInOut)
        .call(() => {
            this.rightTopTween = undefined;
        });
        this.rightBottomTween = Tween.get(this.panels.rightPanelBottom, {override: true})
        .to({x: this.closedPos.rightX}, close.bounceClose, Ease.quadInOut)
        .call(() => {
            this.rightBottomTween = undefined;
        });

        this.leftTopTween.setPosition(position);
        this.leftBottomTween.setPosition(position);
        this.rightTopTween.setPosition(position);
        this.rightBottomTween.setPosition(position);
    }

    animateClosed(): void {

        const {close} = this.services.resources.getSetting(`transitionPanel`);

        this.setToOnScreen();

        this.state = PanelState.CLOSED;
        this.visible = true;

        Tween.get(this, {override: true}).wait(close.wait)
        .call(() => {
            this.animateOut();
        });
    }

    animateOut(position: number = 0) {

        const {close} = this.services.resources.getSetting(`transitionPanel`);

        this.setToOnScreen();

        this.state = PanelState.ANIMATING_OUT;
        this.visible = true;

        if (position === 0) {
            this.signals.transitionOut.dispatch();
            this.signals.transitionOutSound.dispatch();
        }
        this.leftTopTween = Tween.get(this.panels.leftPanelTop, {override: true})
        .to({x: this.openedPos.leftX}, close.open, Ease.quadInOut)
        .call(() => {
            this.leftTopTween = undefined;
            this.signals.animationOutComplete.dispatch();
            this.state = PanelState.PASSIVE;
            this.visible = false;
        });
        this.leftBottomTween = Tween.get(this.panels.leftPanelBottom, {override: true})
        .to({x: this.openedPos.leftX}, close.open, Ease.quadInOut)
        .call(() => {
            this.leftBottomTween = undefined;
        });

        this.rightTopTween = Tween.get(this.panels.rightPanelTop, {override: true})
        .to({x: this.openedPos.rightX}, close.open, Ease.quadInOut)
        .call(() => {
            this.rightTopTween = undefined;
        });
        this.rightBottomTween = Tween.get(this.panels.rightPanelBottom, {override: true})
        .to({x: this.openedPos.rightX}, close.open, Ease.quadInOut)
        .call(() => {
            this.rightBottomTween = undefined;
        });

        this.leftTopTween.setPosition(position);
        this.leftBottomTween.setPosition(position);
        this.rightTopTween.setPosition(position);
        this.rightBottomTween.setPosition(position);
    }

    private calculatePositions(): void {
        const bounds = this.services.layout.getScreenBoundaryInBucketSpace();
        const {left, right, width} = bounds;

        this.closedPos = {
            leftX: left + width / 2 - this.panels.leftPanelTop.width * 0.95,
            rightX: left + width / 2 - this.panels.rightPanelTop.width * 0.05,
        };

        this.openedPos = {
            leftX: left - this.panels.leftPanelTop.width,
            rightX: right,
        };

        this.bouncePos = {
            leftX: this.closedPos.leftX - this.panels.leftPanelTop.width * 0.05,
            rightX: this.closedPos.rightX + this.panels.rightPanelTop.width * 0.05,
        };
    }

    public playFadeTransition(delay?: number | undefined): void {

        this.state = PanelState.CLOSED;
        this.visible = true;

        let tweenDelay = 0;
        if (delay) {
            tweenDelay = delay;
        }

        this.signals.transitionIn.dispatch();

        Tween.get(this).wait(tweenDelay).call(() => {
            this.backgroundContainer.alpha = 0;

            this.setToOnScreen();

            const {fade} = this.services.resources.getSetting(`transitionPanel`);

            this.godRays.visible = true;
            this.godRays.state.setAnimation(0, `god-rays`, false);

            Tween.get(this.backgroundContainer).to({alpha: 1}, fade.time)
            .call(() => {
                this.signals.animationInComplete.dispatch();
            })
            .wait(fade.wait)
            .call(() => {
                this.fadeOut();
            });
        });
    }

    public playCloseTransition(): void {
        this.signals.transitionIn.dispatch();
        this.signals.transitionInSound.dispatch();
        this.godRays.visible = false;
        this.backgroundContainer.alpha = 1;

        this.animateIn();
    }

    public playDragonPandaIntro(): void {

        const {resources} = this.services;
        const {characterIn, characterWait, characterOut} = this.services.resources.getSetting(`transitionPanel`);
        const spec = resources.getSpec(`transitionPanel`);
        const dragonPosIntroY: number = spec.dragonPosIntro.y;
        const pandaPosIntroY: number = spec.pandaPosIntro.y;
        this.characterState = CharacterState.INTRO;

        this.positionCharactersX();

        this.dragonTransition.scale = spec.dragonPosIntro.scale;
        this.pandaTransition.scale = spec.pandaPosIntro.scale;

        const offscreenDragonY = dragonPosIntroY + this.dragonTransition.height;
        const offscreenPandaY = pandaPosIntroY + this.pandaTransition.height;

        this.dragonTransition.visible = true;
        this.pandaTransition.visible = true;

        this.dragonTransition.y = offscreenDragonY;
        this.pandaTransition.y = offscreenPandaY;

        Tween.get(this.dragonTransition).to({y: spec.dragonPosIntro.y}, characterIn)
        .wait(characterWait)
        .to({y: offscreenDragonY}, characterOut);
        Tween.get(this.pandaTransition).to({y: spec.pandaPosIntro.y}, characterIn)
        .wait(characterWait)
        .to({y: offscreenPandaY}, characterOut)
        .call(() => {
            this.dragonTransition.visible = false;
            this.pandaTransition.visible = false;
        });

        this.signals.dragonRoar.dispatch();
        this.signals.pandaRoar.dispatch();
        this.dragonTransition.state.setAnimation(0, `Dragon_Roar`, false);
        this.pandaTransition.state.setAnimation(0, `Panda_Roar`, false);
    }

    public playDragonWin(delay?: number | undefined): void {

        let tweenDelay = 0;
        if (delay) {
            tweenDelay = delay;
        }

        Tween.get(this).wait(tweenDelay).call(() => {
            const {resources} = this.services;
            const spec = resources.getSpec(`transitionPanel`);
            const dragonPosWinY: number = spec.dragonPosWin.y;
            const characterIn: number = this.services.resources.getSetting(`transitionPanel`).characterIn;
            const characterWait: number = this.services.resources.getSetting(`transitionPanel`).characterWait;
            const characterOut: number = this.services.resources.getSetting(`transitionPanel`).characterOut;
            this.characterState = CharacterState.WIN;

            this.positionCharactersX();

            this.dragonTransition.scale = spec.dragonPosWin.scale;
            const offscreenDragonY = dragonPosWinY + this.dragonTransition.height;
            this.dragonTransition.visible = true;
            this.dragonTransition.y = offscreenDragonY;

            Tween.get(this.dragonTransition).to({y: spec.dragonPosWin.y}, characterIn)
            .wait(characterWait + characterOut)
            .call(() => {
                this.dragonTransition.visible = false;
            });

            this.dragonTransition.state.setAnimation(0, `Dragon_Roar`, false);
        });
    }

    public playPandaWin(delay?: number | undefined): void {

        let tweenDelay = 0;
        if (delay) {
            tweenDelay = delay;
        }

        Tween.get(this).wait(tweenDelay).call(() => {
            const {resources} = this.services;
            const spec = resources.getSpec(`transitionPanel`);
            const pandaPosWinY: number = spec.pandaPosWin.y;
            const characterIn: number = this.services.resources.getSetting(`transitionPanel`).characterIn;
            const characterWait: number = this.services.resources.getSetting(`transitionPanel`).characterWait;
            const characterOut: number = this.services.resources.getSetting(`transitionPanel`).characterOut;
            this.characterState = CharacterState.WIN;

            this.positionCharactersX();

            this.pandaTransition.scale = spec.pandaPosWin.scale;
            const offscreenPandaY = pandaPosWinY + this.pandaTransition.height;
            this.pandaTransition.visible = true;
            this.pandaTransition.y = offscreenPandaY;

            Tween.get(this.pandaTransition).to({y: spec.pandaPosWin.y}, characterIn)
            .wait(characterWait + characterOut)
            .call(() => {
                this.pandaTransition.visible = false;
            });

            this.pandaTransition.state.setAnimation(0, `Panda_Roar`, false);
        });
    }

    private fadeOut(): void {

        const {fade} = this.services.resources.getSetting(`transitionPanel`);

        this.signals.transitionOut.dispatch();

        Tween.get(this.backgroundContainer).to({alpha: 0}, fade.time)
        .call(() => {
            this.signals.animationOutComplete.dispatch();
            this.visible = false;
            this.state = PanelState.PASSIVE;
        });
    }

    private positionCharactersX(): void {

        if (!this.dragonTransition || !this.pandaTransition) return;

        const spec = this.services.resources.getSpec(`transitionPanel`);

        if (this.characterState === CharacterState.WIN) {
            this.dragonTransition.x = spec.dragonPosWin.x;
            this.pandaTransition.x = spec.pandaPosWin.x;

            return;
        }

        const {width, height} = this.services.layout.getScreenBoundaryInBucketSpace();
        if (width / height > 1.925) {
            this.dragonTransition.x = spec.dragonPosIntro.x;
            this.pandaTransition.x = spec.pandaPosIntro.x;
        } else {
            this.dragonTransition.x = spec.dragonPosIntroPortrait.x;
            this.pandaTransition.x = spec.pandaPosIntroPortrait.x;
        }
    }

    getInAnim(): boolean {
        if (this.state === PanelState.PASSIVE) {
            return false;
        }

        return true;
    }
}
